import React, { useEffect, useState } from 'react';
import { Collapse, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useHeidiSession } from '../hooks/useHeidiSession';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';

const Wrapper = styled.div<{ $disabled?: boolean }>`
  .ant-collapse {
    background-color: ${({ $disabled }) => ($disabled ? '#f0f0f0' : '#eff6ff')};
    border-color: ${({ $disabled }) => ($disabled ? '#d9d9d9' : '#bfdbfe')};
  }

  .ant-collapse-header {
    align-items: center !important;
  }

  .ant-collapse-content {
    background-color: ${({ $disabled }) => ($disabled ? '#f0f0f0' : '#eff6ff')};
    border-top-color: ${({ $disabled }) => ($disabled ? '#d9d9d9' : '#bfdbfe')};
  }
`;

const HeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Subtitle = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const StyledTextArea = styled(Input.TextArea)`
  font-family: Helvetica, Arial, sans-serif;
  font-size: 13px;
  line-height: 1.42;

  &::placeholder {
    font-size: 12px;
    font-style: italic;
    color: var(--black) !important;
    opacity: 0.3;
  }
`;

const Disclaimer = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const CollapsibleQuickNotes = () => {
  const { t } = useTranslation('common');
  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted }
  } = useSessionAppointment();

  const { status: aiNotesStatus } = useHeidiSession();

  const [notes] = useSessionFormState('QUICK');
  const handleNoteChange = useSessionNoteChangeHandler({ field: 'QUICK' });
  const isDisabled = readOnly || isCompleted;
  const [activeKey, setActiveKey] = useState<string[]>([]);

  useEffect(() => {
    if (notes && activeKey.length === 0) {
      setActiveKey(['quick-notes']);
    }
  }, [notes]);

  if (aiNotesStatus === 'idle') {
    return null;
  }

  const items = [
    {
      key: 'quick-notes',
      label: (
        <HeaderContent>
          <strong>{t('Quick Notes')}</strong>
          <Subtitle>({t('For jotting during consultation')})</Subtitle>
        </HeaderContent>
      ),
      children: (
        <>
          <Form.Item name="QUICK">
            <StyledTextArea
              onChange={(e) => handleNoteChange({ field: 'QUICK', value: e.target.value, skipSanitisation: true })}
              placeholder={t('Jot down quick notes, observations, or reminders while talking to the patient...')}
              rows={5}
              style={{ resize: 'none' }}
              aria-label={t('Quick Notes')}
              disabled={isDisabled}
            />
          </Form.Item>
          <Disclaimer>{t("These notes are temporary and won't be saved to the patient letter")}</Disclaimer>
        </>
      )
    }
  ];

  return (
    <Wrapper $disabled={isDisabled}>
      <Collapse items={items} bordered={false} activeKey={activeKey} onChange={(keys) => setActiveKey(keys)} />
    </Wrapper>
  );
};

export default CollapsibleQuickNotes;
